import { useState, useMemo } from 'react';
import { useAdPerformanceReportQuery } from '@/lib/query-options';
import { AdPerformanceData } from '@/lib/apis';
import { useCurrencySymbol } from '@/hooks/useCurrencySymbol';
import { Skeleton } from '@/components/ui/skeleton';
import { ChevronDown, ChevronRight, Download } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { getFunnelStageLabel } from '@/lib/utils';

interface AdPerformanceReportProps {
  campaignId?: string;
  dateRange?: string;
}

const DATE_RANGE_OPTIONS = [
  { value: 'LAST_7_DAYS', label: 'Last 7 Days' },
  { value: 'LAST_30_DAYS', label: 'Last 30 Days' },
  { value: 'LAST_90_DAYS', label: 'Last 90 Days' },
  { value: 'THIS_MONTH', label: 'This Month' },
  { value: 'LAST_MONTH', label: 'Last Month' },
];

export function AdPerformanceReport({
  campaignId,
  dateRange: initialDateRange = 'LAST_30_DAYS',
}: AdPerformanceReportProps) {
  const [dateRange, setDateRange] = useState<string>(initialDateRange);
  const { data, isLoading, error } = useAdPerformanceReportQuery(campaignId, dateRange);
  const { symbol: currencySymbol } = useCurrencySymbol('google');
  const [statusFilter, setStatusFilter] = useState<string>('all');
  const [adTypeFilter, setAdTypeFilter] = useState<string>('all');
  const [expandedAdGroups, setExpandedAdGroups] = useState<Set<string>>(new Set());

  // Filter data
  const filteredData = useMemo(() => {
    if (!data) return [];
    return data.filter((ad) => {
      if (statusFilter !== 'all' && ad.ad_group_ad_status !== statusFilter) return false;
      if (adTypeFilter !== 'all' && ad.ad_type !== adTypeFilter) return false;
      return true;
    });
  }, [data, statusFilter, adTypeFilter]);

  // Group data by ad group
  const groupedData = useMemo(() => {
    if (!filteredData || filteredData.length === 0) return [];
    
    const groups = new Map<string, AdPerformanceData[]>();
    
    filteredData.forEach((ad) => {
      const adGroupName = ad.ad_group_name || 'Unknown';
      if (!groups.has(adGroupName)) {
        groups.set(adGroupName, []);
      }
      groups.get(adGroupName)!.push(ad);
    });

    // Convert to array and calculate totals for each group
    return Array.from(groups.entries()).map(([adGroupName, ads]) => {
      // Sort ads by funnel stage: bottom, middle, top, then null/undefined
      const funnelStageOrder: Record<string, number> = {
        lower_funnel: 1,
        middle_funnel: 2,
        upper_funnel: 3,
      };

      const sortedAds = [...ads].sort((a, b) => {
        const aOrder = a.funnel_stage ? funnelStageOrder[a.funnel_stage] || 999 : 999;
        const bOrder = b.funnel_stage ? funnelStageOrder[b.funnel_stage] || 999 : 999;
        return aOrder - bOrder;
      });

      const totals = sortedAds.reduce(
        (acc, ad) => ({
          impressions: acc.impressions + ad.impressions,
          clicks: acc.clicks + ad.clicks,
          cost: acc.cost + ad.cost,
          conversions: acc.conversions + (ad.conversions || 0),
          all_conversions: acc.all_conversions + (ad.all_conversions || 0),
          all_conversions_value: acc.all_conversions_value + (ad.all_conversions_value || 0),
          view_through_conversions: acc.view_through_conversions + (ad.view_through_conversions || 0),
        }),
        {
          impressions: 0,
          clicks: 0,
          cost: 0,
          conversions: 0,
          all_conversions: 0,
          all_conversions_value: 0,
          view_through_conversions: 0,
        }
      );

      return {
        adGroupName,
        ads: sortedAds,
        totals,
        ctr: totals.impressions > 0 ? (totals.clicks / totals.impressions) * 100 : 0,
        cpc: totals.clicks > 0 ? totals.cost / totals.clicks : 0,
        cvr: totals.clicks > 0 ? (totals.conversions / totals.clicks) * 100 : 0,
        cpa: totals.conversions > 0 ? totals.cost / totals.conversions : 0,
      };
    });
  }, [filteredData]);

  const toggleAdGroup = (adGroupName: string) => {
    setExpandedAdGroups((prev) => {
      const next = new Set(prev);
      if (next.has(adGroupName)) {
        next.delete(adGroupName);
      } else {
        next.add(adGroupName);
      }
      return next;
    });
  };

  // Get unique statuses and ad types for filters
  const uniqueStatuses = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.map((ad) => ad.ad_group_ad_status).filter(Boolean))).sort();
  }, [data]);

  const uniqueAdTypes = useMemo(() => {
    if (!data) return [];
    return Array.from(new Set(data.map((ad) => ad.ad_type).filter(Boolean))).sort();
  }, [data]);


  const exportToCSV = () => {
    if (!filteredData || filteredData.length === 0) return;

    // Helper function to format funnel stage for CSV
    const formatFunnelStageForCSV = (stage: string | null | undefined, adName: string, adId: string): string => {
      if (!stage) return adName || adId;
      const label = getFunnelStageLabel(stage);
      return label !== stage ? label : (adName || adId);
    };

    const headers = [
      'Funnel Stage',
      'Ad Group',
      'Status',
      'Impressions',
      'Clicks',
      'CTR',
      'Cost',
      'CPC',
      'Conversions',
      'All Conversions',
      'All Conversions Value',
      'View-Through Conversions',
      'CVR',
      'CPA',
    ];

    const csvRows = [headers.join(',')];

    const dataRows = filteredData.map((ad) => {
      const cpa = ad.conversions > 0 ? ad.cost / ad.conversions : 0;
      const funnelStage = formatFunnelStageForCSV(ad.funnel_stage, ad.ad_name, ad.ad_id);
      return [
        `"${funnelStage.replace(/"/g, '""')}"`,
        `"${(ad.ad_group_name || '').replace(/"/g, '""')}"`,
        ad.ad_group_ad_status || '',
        ad.impressions,
        ad.clicks,
        `${(ad.ctr * 100).toFixed(2)}%`,
        ad.cost.toFixed(2),
        ad.cpc.toFixed(2),
        ad.conversions.toFixed(2),
        (ad.all_conversions || 0).toFixed(2),
        (ad.all_conversions_value || 0).toFixed(2),
        (ad.view_through_conversions || 0).toFixed(2),
        `${(ad.cvr * 100).toFixed(2)}%`,
        cpa.toFixed(2),
      ].join(',');
    });

    csvRows.push(...dataRows);
    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const today = new Date().toISOString().split('T')[0];
    link.download = `ad-performance-report-${today}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };


  if (isLoading) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-4 h-[72px]">
          <h3 className="text-lg font-semibold text-gray-500">Ad Performance</h3>
        </div>
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="space-y-4">
              <Skeleton className="h-10 w-full" />
              <Skeleton className="h-64 w-full" />
            </div>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-white shadow-sm border border-red-200 rounded-lg overflow-hidden">
        <div className="p-6 flex items-center gap-3 text-red-600">
          <span className="text-sm">Failed to load ad performance data</span>
        </div>
      </div>
    );
  }

  if (!data || data.length === 0) {
    return (
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between py-4 h-[72px]">
          <h3 className="text-lg font-semibold text-gray-500">Ad Performance</h3>
        </div>
        <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
          <div className="p-6">
            <div className="text-center text-gray-500 py-8">
              <p>No ad performance data available</p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="flex items-center justify-between py-4 h-[72px]">
        <h3 className="text-lg font-semibold text-gray-500">Ad Performance</h3>
        <div className="flex items-center gap-2">
          <Select value={dateRange} onValueChange={(value) => setDateRange(value)}>
            <SelectTrigger className="w-[160px]">
              <SelectValue placeholder="Date range" />
            </SelectTrigger>
            <SelectContent>
              {DATE_RANGE_OPTIONS.map((option) => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={statusFilter} onValueChange={setStatusFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Filter by status" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Statuses</SelectItem>
              {uniqueStatuses.map((status) => (
                <SelectItem key={status} value={status || ''}>
                  {status || ''}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={adTypeFilter} onValueChange={setAdTypeFilter}>
            <SelectTrigger className="w-[140px]">
              <SelectValue placeholder="Filter by type" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All Types</SelectItem>
              {uniqueAdTypes.map((type) => (
                <SelectItem key={type} value={type || ''}>
                  {(type || '').replace('AdType.', '')}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button variant="outline" size="sm" onClick={exportToCSV} className="gap-2">
            <Download className="h-4 w-4" />
            Export CSV
          </Button>
        </div>
      </div>

      {/* Table */}
      <div className="bg-white shadow-sm border border-gray-200 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-white">
              <tr>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider w-8">
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Ad Group
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Impressions
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Clicks
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Cost
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  Conversions
                </th>
                <th className="px-6 py-3 text-left text-xs montserrat-medium text-gray-500 uppercase tracking-wider">
                  CPA
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {groupedData.map((group) => {
                const isExpanded = expandedAdGroups.has(group.adGroupName);
                
                return (
                  <>
                    {/* Ad Group Row */}
                    <tr key={group.adGroupName} className="hover:bg-gray-50 bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap">
                        <button
                          onClick={() => toggleAdGroup(group.adGroupName)}
                          className="text-gray-500 hover:text-gray-700"
                        >
                          {isExpanded ? (
                            <ChevronDown className="h-4 w-4" />
                          ) : (
                            <ChevronRight className="h-4 w-4" />
                          )}
                        </button>
                      </td>
                      <td className="px-6 py-4">
                        <div className="text-base font-medium text-gray-900">{group.adGroupName}</div>
                        <div className="text-xs text-gray-500 mt-1">{group.ads.length} ads</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-sm montserrat-medium text-gray-900">-</span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm montserrat-medium text-gray-900">
                          {group.totals.impressions.toLocaleString()}
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm montserrat-medium text-gray-900">
                          {group.totals.clicks.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">CTR: {group.ctr.toFixed(2)}%</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm montserrat-medium text-gray-900">
                          {currencySymbol}{group.totals.cost.toFixed(2)}
                        </div>
                        <div className="text-xs text-gray-500">CPC: {currencySymbol}{group.cpc.toFixed(2)}</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm montserrat-medium text-gray-900">
                          {group.totals.conversions.toLocaleString()}
                        </div>
                        <div className="text-xs text-gray-500">Rate: {group.cvr.toFixed(2)}%</div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <div className="text-sm montserrat-medium text-gray-900">
                          {group.totals.conversions > 0 ? `${currencySymbol}${group.cpa.toFixed(2)}` : '-'}
                        </div>
                      </td>
                    </tr>
                    {/* Ad Rows (nested under ad group) */}
                    {isExpanded && group.ads.map((ad) => {
                      const adCtr = ad.impressions > 0 ? (ad.clicks / ad.impressions) * 100 : 0;
                      const adCpc = ad.clicks > 0 ? ad.cost / ad.clicks : 0;
                      const adCvr = ad.clicks > 0 ? (ad.conversions / ad.clicks) * 100 : 0;
                      const adCpa = ad.conversions > 0 ? ad.cost / ad.conversions : 0;

                      // Determine status pill colors
                      const getStatusPillClasses = (status: string | null | undefined) => {
                        if (!status) return 'bg-gray-100 text-gray-900';
                        switch (status.toUpperCase().trim()) {
                          case 'ENABLED':
                            return 'bg-green-100 text-green-800';
                          case 'PAUSED':
                            return 'bg-gray-100 text-gray-900';
                          case 'REMOVED':
                            return 'bg-yellow-100 text-yellow-800';
                          default:
                            return 'bg-gray-100 text-gray-900';
                        }
                      };

                      // Format status text: trim and capitalize first letter
                      const formatStatusText = (status: string | null | undefined) => {
                        if (!status) return '';
                        const trimmed = status.trim();
                        if (!trimmed) return '';
                        return trimmed.charAt(0).toUpperCase() + trimmed.slice(1).toLowerCase();
                      };

                      // Format funnel stage for display
                      const formatFunnelStage = (stage: string | null | undefined): string => {
                        if (!stage) return ad.ad_name || ad.ad_id;
                        const label = getFunnelStageLabel(stage);
                        return label !== stage ? label : (ad.ad_name || ad.ad_id);
                      };

                      return (
                        <tr key={ad.ad_id} className="hover:bg-gray-50">
                          <td className="px-6 py-4 whitespace-nowrap">
                          </td>
                          <td className="px-6 py-4">
                            <div className="text-sm font-medium text-gray-900 pl-6">
                              {formatFunnelStage(ad.funnel_stage)}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            {ad.ad_group_ad_status ? (
                              <span className={`inline-flex items-center justify-center whitespace-nowrap px-3.5 py-0.5 rounded-full text-xs font-medium ${getStatusPillClasses(ad.ad_group_ad_status)}`}>
                                {formatStatusText(ad.ad_group_ad_status)}
                              </span>
                            ) : (
                              <span className="text-sm montserrat-medium text-gray-900">-</span>
                            )}
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm montserrat-medium text-gray-900">
                              {ad.impressions.toLocaleString()}
                            </div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm montserrat-medium text-gray-900">
                              {ad.clicks.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">CTR: {adCtr.toFixed(2)}%</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm montserrat-medium text-gray-900">
                              {currencySymbol}{ad.cost.toFixed(2)}
                            </div>
                            <div className="text-xs text-gray-500">CPC: {currencySymbol}{adCpc.toFixed(2)}</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm montserrat-medium text-gray-900">
                              {ad.conversions.toLocaleString()}
                            </div>
                            <div className="text-xs text-gray-500">Rate: {adCvr.toFixed(2)}%</div>
                          </td>
                          <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm montserrat-medium text-gray-900">
                              {ad.conversions > 0 ? `${currencySymbol}${adCpa.toFixed(2)}` : '-'}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
}
